import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import "../styles/global.css";
import "../styles/blog.css";
import { Helmet } from "react-helmet";
const Blog = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Blog";
    // Initialize AdSense
    try {
      if (window.adsbygoogle && process.env.NODE_ENV !== "development") {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, []);

  const categories = [
    { id: "all", name: "All Articles" },
    { id: "communication", name: "Communication" },
    { id: "growth", name: "Personal Growth" },
    { id: "red-flags", name: "Red Flags" },
    { id: "healing", name: "Healing & Recovery" },
  ];

  const blogPosts = [
    {
      id: "communication-tips",
      title: "Communication Tips For Deeper Connection",
      excerpt:
        "Learn practical strategies to improve understanding and intimacy with your partner.",
      date: "June 15, 2023",
      author: "Dr. Emily Chen",
      category: "communication",
      image: "assets/images/communication-tips.jpeg",
      featured: true,
    },
    {
      id: "toxic-relationship-signs",
      title: "7 Signs You Might Be in a Toxic Relationship",
      excerpt:
        "Recognizing the warning signs early can help you make informed decisions about your relationship.",
      date: "May 28, 2023",
      author: "Marcus Johnson",
      category: "red-flags",
      image: "assets/images/toxic-signs.jpeg",
      featured: true,
    },
    {
      id: "self-care-guide",
      title: "Self-Care Guide For Relationship Transitions",
      excerpt:
        "Essential practices to support your wellbeing during relationship changes.",
      date: "April 12, 2023",
      author: "Sophia Williams",
      category: "healing",
      image: "assets/images/self-care-guide.jpeg",
      featured: true,
    },
    {
      id: "healthy-boundaries",
      title: "Setting Healthy Boundaries in Relationships",
      excerpt:
        "Learn how to establish and maintain boundaries that honor your needs while respecting others.",
      date: "March 30, 2023",
      author: "Dr. Emily Chen",
      category: "growth",
      image: "assets/images/boundaries.jpeg",
      featured: false,
    },
    {
      id: "difficult-conversations",
      title: "How to Navigate Difficult Conversations With Your Partner",
      excerpt:
        "Strategies for discussing challenging topics while maintaining connection and respect.",
      date: "March 15, 2023",
      author: "Marcus Johnson",
      category: "communication",
      image: "assets/images/difficult-conversations.jpeg",
      featured: false,
    },
    {
      id: "breakup-healing",
      title: "The Science of Healing After a Breakup",
      excerpt:
        "Understanding the psychological process of recovery and how to support your emotional wellbeing.",
      date: "February 22, 2023",
      author: "Sophia Williams",
      category: "healing",
      image: "assets/images/breakup-healing.jpeg",
      featured: false,
    },
    {
      id: "emotional-intelligence",
      title: "Building Emotional Intelligence in Relationships",
      excerpt:
        "How developing self-awareness and empathy can transform your connections with others.",
      date: "February 10, 2023",
      author: "Dr. Emily Chen",
      category: "growth",
      image: "assets/images/emotional-intelligence.jpeg",
      featured: false,
    },
    {
      id: "gaslighting",
      title: "Recognizing Gaslighting in Relationships",
      excerpt:
        "How to identify this subtle form of manipulation and protect your sense of reality.",
      date: "January 25, 2023",
      author: "Marcus Johnson",
      category: "red-flags",
      image: "assets/images/gaslighting.jpeg",
      featured: false,
    },
    {
      id: "conflict-resolution",
      title: "Healthy Conflict Resolution Techniques",
      excerpt:
        "Turn disagreements into opportunities for growth and deeper connection.",
      date: "January 12, 2023",
      author: "Sophia Williams",
      category: "communication",
      image: "assets/images/conflict-resolution.jpeg",
      featured: false,
    },
    {
      id: "moving-forward",
      title: "Moving Forward: Finding Joy After A Relationship Ends",
      excerpt:
        "Perspectives on healing and rediscovering happiness in your next chapter.",
      date: "December 18, 2022",
      author: "Dr. Emily Chen",
      category: "healing",
      image: "assets/images/moving-forward.jpeg",
      featured: false,
    },
    {
      id: "relationship-goals",
      title: "Setting Healthy Relationship Goals Together",
      excerpt:
        "How to create shared visions that strengthen your bond and future together.",
      date: "December 5, 2022",
      author: "Marcus Johnson",
      category: "growth",
      image: "assets/images/relationship-goals.jpeg",
      featured: false,
    },
    {
      id: "love-bombing",
      title: "Understanding Love Bombing: When Affection Is a Red Flag",
      excerpt:
        "How to distinguish between genuine affection and manipulative tactics in new relationships.",
      date: "November 20, 2022",
      author: "Sophia Williams",
      category: "red-flags",
      image: "assets/images/love-bombing.jpeg",
      featured: false,
    },
  ];

  const featuredPosts = blogPosts.filter((post) => post.featured);

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory =
      activeCategory === "all" || post.category === activeCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  return (
    <div className="blog-container">
      <Header />
      <Helmet>
        <title>Blog</title>
        <meta
          name="description"
          content="Our Blog page that contains various blogs regarding relationships."
        />
      </Helmet>
      <section className="blog-hero">
        <div className="blog-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            Relationship Insights Blog
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lead-text">
            Evidence-based articles to help you navigate relationships with
            clarity and confidence.
          </motion.p>

          <motion.div
            className="search-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}>
            <input
            aria-label="Search articles"
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button className="search-button">
              <i className="fas fa-search"> search</i>
            </button>
          </motion.div>
        </div>
      </section>

      <section className="featured-posts">
        <div className="container">
          <h2 className="section-title">Featured Articles</h2>

          <div className="featured-grid">
            {featuredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                className="featured-post-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}>
                <div className="featured-post-image">
                  <img src={post.image} alt={post.title} />
                  <div className="category-badge">
                    {categories.find((c) => c.id === post.category).name}
                  </div>
                </div>
                <div className="featured-post-content">
                  <h3>{post.title}</h3>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <div className="post-meta">
                    <span className="post-author">{post.author}</span>
                    <span className="post-date" style={{color:"#000"}}>{post.date}</span>
                  </div>
                  <Link to={`/blog/${post.id}`} className="read-more-link">
                    <span>Read Article</span>
                    <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Ad Placement */}
      <div className="container">
        <div className="ad-container blog-top-ad"  style={{width: "100%", minWidth: "300px"}}>
          {/* <div className="ad-label">Advertisement</div> */}
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="1234567898"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>
      </div>

      <section className="all-posts">
        <div className="container">
          <div className="blog-main-container">
            <div className="blog-main-content">
              <div className="posts-header">
                <h2 className="section-title">All Articles</h2>

                <div className="category-filters">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      className={`category-filter ${
                        activeCategory === category.id ? "active" : ""
                      }`}
                      onClick={() => setActiveCategory(category.id)}>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {filteredPosts.length === 0 ? (
                <div className="no-results">
                  <i className="fas fa-search"></i>
                  <h3>No articles found</h3>
                  <p>Try adjusting your search or filter criteria</p>
                  <button
                    className="reset-button"
                    onClick={() => {
                      setSearchTerm("");
                      setActiveCategory("all");
                    }}>
                    Reset Filters
                  </button>
                </div>
              ) : (
                <div className="posts-grid">
                  {filteredPosts.map((post, index) => (
                    <React.Fragment key={post.id}>
                      <motion.div
                        className="post-card"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.05 * index }}>
                        <div className="post-image">
                          <img src={post.image} alt={post.title} />
                          <div className="category-tag"  style={{color:"#FFF", backgroundColor:"#2177c5"}}>
                            {
                              categories.find((c) => c.id === post.category)
                                .name
                            }
                          </div>
                        </div>
                        <div className="post-content">
                          <h3>{post.title}</h3>
                          <p className="post-excerpt">{post.excerpt}</p>
                          <div className="post-meta">
                            <span className="post-author">{post.author}</span>
                            <span className="post-date" style={{color:"#000"}}>{post.date}</span>
                          </div>
                          <Link
                            to={`/blog/${post.id}`}
                            className="read-more-link">
                            <span>Read Article</span>
                            <i className="fas fa-arrow-right"></i>
                          </Link>
                        </div>
                      </motion.div>

                      {/* Mid-page Ad after every 4 posts */}
                      {index === 3 && (
                        <div className="ad-container blog-mid-ad"  style={{width: "100%", minWidth: "300px"}}>
                          {/* <div className="ad-label">Advertisement</div> */}
                          <ins
                            className="adsbygoogle"
                            style={{ display: "block" }}
                            data-ad-client="ca-pub-2067522743161930"
                            data-ad-slot="1234567899"
                            data-ad-format="auto"
                            data-full-width-responsive="true"></ins>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            <div className="blog-sidebar">
              <div className="sidebar-widget">
                <h3>Popular Tags</h3>
                <div className="tag-cloud">
                  <a href="#" className="tag">
                    Communication
                  </a>
                  <a href="#" className="tag">
                    Self-Care
                  </a>
                  <a href="#" className="tag">
                    Boundaries
                  </a>
                  <a href="#" className="tag">
                    Healing
                  </a>
                  <a href="#" className="tag">
                    Red Flags
                  </a>
                  <a href="#" className="tag">
                    Conflict Resolution
                  </a>
                  <a href="#" className="tag">
                    Self-Worth
                  </a>
                  <a href="#" className="tag">
                    Trust
                  </a>
                  <a href="#" className="tag">
                    Breakups
                  </a>
                </div>
              </div>

              {/* Sidebar Ad */}
              <div className="ad-container blog-sidebar-ad"  style={{width: "100%", minWidth: "300px"}}>
                {/* <div className="ad-label">Advertisement</div> */}
                <ins
                  className="adsbygoogle"
                  style={{ display: "block" }}
                  data-ad-client="ca-pub-2067522743161930"
                  data-ad-slot="1234567900"
                  data-ad-format="auto"
                  data-full-width-responsive="true"></ins>
              </div>

              {/* <div className="sidebar-widget">
                <h3>Meet Our Authors</h3>
                <div className="authors-list">
                  <div className="author-item">
                    <img
                      src="assets/images/team-emily.jpeg"
                      alt="Dr. Emily Chen"
                    />
                    <div className="author-info">
                      <h4>Dr. Emily Chen</h4>
                      <p>Relationship Psychologist</p>
                    </div>
                  </div>
                  <div className="author-item">
                    <img
                      src="assets/images/team-marcus.jpeg"
                      alt="Marcus Johnson"
                    />
                    <div className="author-info">
                      <h4>Marcus Johnson</h4>
                      <p>Research Director</p>
                    </div>
                  </div>
                  <div className="author-item">
                    <img
                      src="assets/images/team-sophia.jpeg"
                      alt="Sophia Williams"
                    />
                    <div className="author-info">
                      <h4>Sophia Williams</h4>
                      <p>Content Strategist</p>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* <div className="sidebar-widget newsletter">
                <h3>Get Relationship Insights</h3>
                <p>Join our newsletter for expert advice and support</p>
                <form className="newsletter-form">
                  <input type="email" placeholder="Your email address" />
                  <button type="submit">Subscribe</button>
                </form>
              </div> */}
            </div>
          </div>

          {/* Bottom Ad Placement */}
          <div className="ad-container blog-bottom-ad"  style={{width: "100%", minWidth: "300px"}}>
            {/* <div className="ad-label">Advertisement</div> */}
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="1234567901"
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
          </div>
        </div>
      </section>

      <section className="blog-cta">
        <div className="container">
          <div className="cta-box">
            <div className="cta-content">
              <h2>Wondering About Your Relationship?</h2>
              <p style={{color:"#FFF"}}>
                Take our research-backed assessment to gain personalized
                insights about your relationship's health and potential.
              </p>
              <Link to="/quiz" className="cta-button">
                <span>Take Free Assessment</span>
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <h3 style={{color:"#FFF"}}>Relationship Wellness</h3>
              <p style={{color:"#FFF"}}>Evidence-based insights for healthier relationships.</p>
              <div className="social-icons">
                <a href="#" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="#" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="#" aria-label="Twitter">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" aria-label="LinkedIn">
                  <i className="fab fa-linkedin-in"></i>
                </a>
              </div>
            </div>
            <div className="footer-links">
              <h4 style={{color:"#FFF"}}>Quick Links</h4>
              <ul>
                <li>
                  <a href="/">Home</a>
                </li>
                <li>
                  <a href="/about">About Us</a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
                <li>
                  <a href="/resources">Resources</a>
                </li>
                <li>
                  <a href="/contact">Contact</a>
                </li>
              </ul>
            </div>
            <div className="footer-contact">
              <h4 style={{color:"#FFF"}}>Contact Us</h4>
              <p>
                <i className="fas fa-envelope"></i> support@nixexa.net
              </p>
              <p>
                <i className="fas fa-phone"></i> (555) 123-4567
              </p>
              <p>
                <i className="fas fa-map-marker-alt"></i> 123 Wellness Ave,
                Suite 200
                <br />
                San Francisco, CA 94110
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="copyright">
              © {new Date().getFullYear()} Relationship Wellness. All rights
              reserved.
            </p>
            <div className="legal-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
